import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { type AssignableAdmin, type Portfolio } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * `GET /api/v1/admin/portfolios` - who manages how many merchants (superadmin only). A read of cross-organisation data, so it needs a
 * stated reason, like every other read of this console (the default one, or the one typed); the query stays disabled without one.
 */
export function usePortfoliosQuery(reason: string): UseQueryResult<readonly Portfolio[], ApiError> {
  const { accessToken } = useSession();
  const reasonValid = reason.trim().length >= 10;

  return useQuery({
    queryKey: ['portfolios', reason],
    queryFn: async () => {
      const params = new URLSearchParams({ reason });
      const body = await apiRequest<{ portfolios: readonly Portfolio[] }>(
        `/admin/portfolios?${params.toString()}`,
        {
          accessToken,
        },
      );
      return body.portfolios;
    },
    enabled: accessToken !== null && reasonValid,
  });
}

/**
 * `GET /api/v1/admin/managers` - the ACTIVE administrators a shop can be assigned to: the picker of « Nouveau marchand »,
 * « Attribuer » and « Transférer ». Superadmin only (`enabled` says so; the server decides), no typed reason (the read is audited with
 * a fixed purpose).
 */
export function useAssignableAdminsQuery(
  enabled: boolean,
): UseQueryResult<readonly AssignableAdmin[], ApiError> {
  const { accessToken } = useSession();

  return useQuery({
    queryKey: ['managers'],
    queryFn: async () => {
      const body = await apiRequest<{ managers: readonly AssignableAdmin[] }>('/admin/managers', {
        accessToken,
      });
      return body.managers;
    },
    enabled: enabled && accessToken !== null,
  });
}

/**
 * `POST /api/v1/admin/merchants/reassign` - a superadmin assigns (or moves) 1 to 100 shops to ONE administrator. The superadmin's OWN
 * password travels in the body and is consumed by the API's re-authentication guard; `gcTime: 0` so the variables do not outlive the call.
 */
export function useReassignMerchantsMutation(): UseMutationResult<
  { moved: number },
  ApiError,
  { tenantIds: readonly string[]; toAdminId: string; reason: string; confirmPassword: string }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ tenantIds, toAdminId, reason, confirmPassword }) =>
      apiRequest<{ moved: number }>('/admin/merchants/reassign', {
        method: 'POST',
        body: { tenantIds, toAdminId, reason, confirmPassword },
        accessToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['merchants'] });
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
    },
  });
}

/** `POST /api/v1/admin/portfolios/:adminId/transfer` - a superadmin moves a WHOLE portfolio to another administrator. */
export function useTransferPortfolioMutation(): UseMutationResult<
  { moved: number },
  ApiError,
  { fromAdminId: string; toAdminId: string; reason: string; confirmPassword: string }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ fromAdminId, toAdminId, reason, confirmPassword }) =>
      apiRequest<{ moved: number }>(`/admin/portfolios/${fromAdminId}/transfer`, {
        method: 'POST',
        body: { toAdminId, reason, confirmPassword },
        accessToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['merchants'] });
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
    },
  });
}
